import {
	Dtos,
	type FinancialPeriodPreparationMethod,
	type ISODate,
	isWipExtractionFinished,
	PeriodType,
	type WorkInProgressScheduleExtractionId,
} from "@inrev/common";
import { Option } from "@inrev/core";
import { DateTime } from "luxon";
import { type PeriodYearGroup } from "../../patterns/period-selector";
import { compareExtractions, scheduleOfRecordExtractionIds } from "./wipPeriod";

export const WIP_ANALYSIS_PERIOD_SELECTOR_UNDATED_YEAR_KEY = "Undated";

export type WipAnalysisPeriodSelectorItem = {
	extractionId: WorkInProgressScheduleExtractionId;
	statementDate: ISODate | null;
	isScheduleOfRecord: boolean;
	periodType: PeriodType | null;
	preparationMethod: FinancialPeriodPreparationMethod | null;
};

const finishedExtractions = (
	extractions: ReadonlyArray<Dtos.Wip.Extraction.Get.Response>,
): Dtos.Wip.Extraction.Get.Response[] =>
	extractions.filter(isWipExtractionFinished).sort(compareExtractions("newest-first"));

const toPeriodSelectorItem = (
	extraction: Dtos.Wip.Extraction.Get.Response,
	scheduleOfRecordIds: ReadonlySet<WorkInProgressScheduleExtractionId>,
): WipAnalysisPeriodSelectorItem => ({
	extractionId: extraction.id,
	statementDate: Option.toNull(extraction.statementDate),
	isScheduleOfRecord: scheduleOfRecordIds.has(extraction.id),
	periodType: Option.toNull(extraction.periodType),
	preparationMethod: Option.toNull(extraction.preparationMethod),
});

export const buildWipAnalysisPeriodSelectorItems = (
	extractions: ReadonlyArray<Dtos.Wip.Extraction.Get.Response>,
	statements: ReadonlyArray<Dtos.Wip.Statement.Get.Response>,
): WipAnalysisPeriodSelectorItem[] => {
	const scheduleOfRecordIds = scheduleOfRecordExtractionIds(statements);
	return finishedExtractions(extractions).map((extraction) =>
		toPeriodSelectorItem(extraction, scheduleOfRecordIds),
	);
};

export const defaultWipAnalysisPeriodSelectorExtractionIds = (
	items: ReadonlyArray<WipAnalysisPeriodSelectorItem>,
): ReadonlySet<WorkInProgressScheduleExtractionId> =>
	new Set(items.filter((item) => item.isScheduleOfRecord).map((item) => item.extractionId));

const yearKeyForItem = (item: WipAnalysisPeriodSelectorItem): string =>
	Option.isNone(item.statementDate)
		? WIP_ANALYSIS_PERIOD_SELECTOR_UNDATED_YEAR_KEY
		: DateTime.fromISO(item.statementDate).toFormat("yyyy");

const compareYearGroupKeys = (left: string, right: string): number => {
	if (left === WIP_ANALYSIS_PERIOD_SELECTOR_UNDATED_YEAR_KEY) return 1;
	if (right === WIP_ANALYSIS_PERIOD_SELECTOR_UNDATED_YEAR_KEY) return -1;
	return Number(right) - Number(left);
};

export const groupWipAnalysisPeriodSelectorItemsByYear = (
	items: ReadonlyArray<WipAnalysisPeriodSelectorItem>,
): PeriodYearGroup<WipAnalysisPeriodSelectorItem>[] => {
	const itemsByYear = new Map<string, WipAnalysisPeriodSelectorItem[]>();

	for (const item of items) {
		const yearKey = yearKeyForItem(item);
		const yearItems = itemsByYear.get(yearKey) ?? [];
		yearItems.push(item);
		itemsByYear.set(yearKey, yearItems);
	}

	return [...itemsByYear.entries()]
		.sort(([left], [right]) => compareYearGroupKeys(left, right))
		.map(([yearKey, items]) => ({ yearKey, items }));
};
